import { storeToRefs } from 'pinia';
import { setupLayouts } from 'virtual:generated-layouts';
import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '~/stores/useAuthStore';
import { useSplashStore } from '~/stores/useSplashStore';
import FeatureFlagService from '~/services/FeatureFlagService';
import { layoutMiddleware } from '~/middlewares/layout.middleware';

import AuthService from '~/services/authService';
import routes from '~pages';

const router = createRouter({
  history: createWebHistory(),
  routes: setupLayouts(routes),
});

const { beforeEach } = router;

// Estado global para controlar splash screen
let splashShownForOfflineUser = false;
let splashShownForOnlineUser = false;
const SPLASH_DURATION = 3000; // 3 segundos

beforeEach(async (to, from) => {
  const AUTH_STORE = useAuthStore();
  const { session } = storeToRefs(AUTH_STORE);
  const { CallIsAuthenticated } = AuthService;
  const isActiveFeature = computed(() => {
    if (FeatureFlagService !== null) {
      const { currentEconomicGroupMember } = AUTH_STORE.session;
      const whiteList = FeatureFlagService.findByFlagName('WhiteListCommissions');

      return (
        FeatureFlagService?.isOn('CommissionsFeaturesAvailable') ||
        (Array.isArray(whiteList?.defaultValue) &&
          whiteList.defaultValue.includes(currentEconomicGroupMember))
      );
    }

    return false;
  });

  const shouldRedirectToDashboard = computed<boolean>(() => {
    return (
      !isActiveFeature.value &&
      (to.name === 'commissions' ||
        to.name === 'my-account-redemptions' ||
        to.name === 'my-account-balance-statement')
    );
  });

  layoutMiddleware(to, from);

  if (from.fullPath === '/' && to.fullPath === '/') {
    if (CallIsAuthenticated() === false) return true;
  }
  if (CallIsAuthenticated() === false) return await handleOfflineUser(to, from);
  else return await handleOnlineUser(to, from);

  /**
   * Lida com usuários offline mostrando splash screen antes do redirecionamento
   */
  async function handleOfflineUser(to: any, from: any) {
    if (to.meta.isAuthRequired === false) {
      // Para rotas de registro, não mostrar splash desnecessariamente
      const isRegisterRoute =
        to.name?.toString().includes('register') ||
        to.name?.toString().includes('access-control') ||
        to.name?.toString().includes('access-control-solicitationCode');

      if (isRegisterRoute) {
        return true; // Permite acesso direto à rota de registro
      }

      // Verificar se há uma rota de registro armazenada após logout
      if (typeof window !== 'undefined') {
        const intendedRoute = sessionStorage.getItem('intendedRegistrationRoute');
        if (intendedRoute && intendedRoute !== to.fullPath) {
          sessionStorage.removeItem('intendedRegistrationRoute');
          router.push(intendedRoute);
          return false;
        }
      }

      if (shouldShowSplash(from, to) && !splashShownForOfflineUser) {
        splashShownForOfflineUser = true;

        switch (session.value.sessionStatus) {
          case 'WINDOW_CLOSED': {
            await showSplashThenRedirect(() => router.push({ name: 'welcome-back' }));
            return false;
          }
          case 'LOGGED_OUT': {
            await showSplashThenRedirect(() => router.push({ name: 'goodbye' }));
            return false;
          }
          default:
            await showSplashThenRedirect(() => router.push({ name: 'welcome' }));
            return false;
        }
      }

      return true;
    }

    if (shouldShowSplash(from, to) && !splashShownForOfflineUser) {
      splashShownForOfflineUser = true;

      await showSplashThenRedirect(() => {
        router.push({ name: 'welcome' });
      });

      return false;
    }

    return { name: 'welcome' };
  }

  /**
   * Lida com usuários online com controle de splash screen
   */
  async function handleOnlineUser(to: any, from: any) {
    const isDashboard = to.name === 'dashboard';
    const isNotAllowed = to.name === 'not-allowed';

    if (to.meta.isAuthRequired) {
      switch (session.value.sessionStatus) {
        case 'NOT_ALLOWED':
        case 'FORBIDDEN':
        case 'UNAVAILABLE':
          if (!isNotAllowed) {
            if (shouldShowSplash(from, to) && !splashShownForOnlineUser) {
              splashShownForOnlineUser = true;
              await showSplashThenRedirect(() => {
                router.push({ name: 'not-allowed' });
              });
              return false;
            }
            return { name: 'not-allowed' };
          }
          return true;

        default:
          if (shouldRedirectToDashboard.value && !isDashboard) {
            return { name: 'dashboard' };
          }
          break;
      }
    }

    const isRegisterRoute = ['register', 'access-control', 'access-control-solicitationCode'].some(
      (keyword) => to.name?.toString().includes(keyword),
    );

    if (isRegisterRoute) {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('intendedRegistrationRoute', to.fullPath);
      }
      return true;
    }

    if (!isDashboard && shouldShowSplash(from, to) && !splashShownForOnlineUser) {
      splashShownForOnlineUser = true;
      await showSplashThenRedirect(() => {
        router.push({ name: 'dashboard' });
      });
      return false;
    }

    if (shouldRedirectToDashboard.value && !isDashboard) {
      return { name: 'dashboard' };
    }

    return true;
  }

  /**
   * Determina se deve mostrar splash screen
   */
  function shouldShowSplash(from: any, to: any): boolean {
    return (
      from.fullPath === '/' ||
      from.name === undefined ||
      (from.name === 'index' && to.name !== 'index')
    );
  }

  /**
   * Mostra splash screen e executa callback após o delay
   */
  async function showSplashThenRedirect(callback: () => void): Promise<void> {
    const targetRoute = { name: 'index' };

    if (router.currentRoute.value.name !== 'index') {
      router.push(targetRoute);
    }

    const splashStore = useSplashStore();

    const isAuthenticated = CallIsAuthenticated();
    const message = isAuthenticated
      ? 'redirecionando para o painel...'
      : 'você será redirecionado em instantes...';

    splashStore.showSplash(message, SPLASH_DURATION, callback);
  }
});

export default router;
